import { useNavigate } from 'react-router-dom'
import { Offline } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { ConnectedAccount } from '@/settings/schemas/external-storage-schema'
import { ConnectedDmsRow } from '@/matter/components/matter-settings/ConnectedDmsRow'
import { MatterFolderSyncRow } from '@/matter/components/matter-settings/MatterFolderSyncRow'

interface MatterSettingsConnectedAccountCardProps {
  canChooseFolder: boolean
  driveSyncEnabled: boolean
  error: string | null
  loading: boolean
  onChooseFolder: () => void
  onDisconnectFolder: () => void
  disconnectingFolder?: boolean
  syncedFolderName: string | null
  syncAccount: ConnectedAccount | null
}

export function MatterSettingsConnectedAccountCard({
  canChooseFolder,
  driveSyncEnabled,
  error,
  loading,
  onChooseFolder,
  onDisconnectFolder,
  disconnectingFolder = false,
  syncedFolderName,
  syncAccount,
}: MatterSettingsConnectedAccountCardProps) {
  const navigate = useNavigate()

  return (
    <div className="w-full max-w-[946px] overflow-hidden rounded-lg border border-brand-neutral-200 bg-white">
      <div className="flex min-h-11 items-center gap-1 border-b border-brand-neutral-200 px-4 py-2">
        <p className="min-w-0 flex-1 font-rethink-sans text-sm font-semibold leading-5 text-brand-neutral-500">Connected Account</p>
      </div>

      {loading && (
        <div className="flex justify-center px-4 py-8">
          <CircularProgressContinuousSized size={24} thickness={7} />
        </div>
      )}

      {!loading && error && <p className="px-4 py-6 text-sm text-red-600">{error}</p>}

      {!loading && !error && !driveSyncEnabled && <p className="px-4 py-6 text-sm text-brand-neutral-600">Document management integrations are not enabled.</p>}

      {!loading && !error && driveSyncEnabled && syncAccount == null && (
        <div className="flex items-center gap-4 px-4 py-4">
          <div className="flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-md border border-brand-neutral-200 bg-white">
            <Offline className="h-6 w-6 text-true-gray-300" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-rethink-sans text-base font-semibold leading-7 text-brand-neutral-950">No Connected Account</p>
            <p className="truncate text-sm leading-5 tracking-[-0.15px] text-brand-neutral-600">
              Connect a document management system to sync a folder to this matter.
            </p>
          </div>
          <Button type="button" variant="tonal" color="primary" size="sm" className="shrink-0" onClick={() => navigate('/dashboard/settings')}>
            Settings
          </Button>
        </div>
      )}

      {!loading && !error && driveSyncEnabled && syncAccount != null && (
        <>
          <ConnectedDmsRow account={syncAccount} />
          <MatterFolderSyncRow
            account={syncAccount}
            canChooseFolder={canChooseFolder}
            disconnecting={disconnectingFolder}
            syncedFolderName={syncedFolderName}
            onChooseFolder={onChooseFolder}
            onDisconnectFolder={onDisconnectFolder}
          />
        </>
      )}
    </div>
  )
}
